
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/lib/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  isAdminRoute?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  isAdminRoute
}) => {
  const { user, profile, isLoading, session } = useAuth();

  // Show loading state while auth state is being determined
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated OR session is missing/expired
  if (!user || !session) {
    console.log('ProtectedRoute: No valid session, redirecting to /auth', {
      hasUser: !!user,
      hasSession: !!session,
      sessionExpiry: session?.expires_at
    });
    return <Navigate to="/auth" replace />;
  }

  // Redirect to profile completion if first name is missing
  if (!profile?.first_name) {
    return <Navigate to="/complete-profile" replace />;
  }

  // Check if this is an admin route and the user is not an admin
  if (isAdminRoute && profile?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // If a specific role is required and user doesn't have it
  if (requiredRole && profile?.role !== requiredRole && profile?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
